import { Bold, Italic, Link2, List, ListOrdered, Paperclip, Quote, Underline } from "lucide-react";
import { useRef, useState } from "react";

const tools = [
  { label: "Heading 2", text: "H2", before: "## ", after: "" },
  { label: "Heading 3", text: "H3", before: "### ", after: "" },
  { label: "Bold", icon: Bold, before: "**", after: "**" },
  { label: "Italic", icon: Italic, before: "_", after: "_" },
  { label: "Underline", icon: Underline, before: "<u>", after: "</u>" },
  { label: "Bulleted list", icon: List, before: "- ", after: "" },
  { label: "Numbered list", icon: ListOrdered, before: "1. ", after: "" },
  { label: "Quote", icon: Quote, before: "> ", after: "" },
];

export function RichTextEditor({
  label = "Your view",
  placeholder,
  value: controlledValue,
  onChange,
  compact = false,
}: {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  compact?: boolean;
}) {
  const [uncontrolledValue, setUncontrolledValue] = useState("");
  const [files, setFiles] = useState<string[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const value = controlledValue ?? uncontrolledValue;
  const setValue = onChange ?? setUncontrolledValue;

  const wrapSelection = (before: string, after: string) => {
    const input = inputRef.current;
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const selected = value.slice(start, end) || "text";
    const next = `${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`;
    setValue(next);
    window.requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  const insertLink = () => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || "link text";
    const next = `${value.slice(0, start)}[${selected}](https://)${value.slice(end)}`;
    setValue(next);
  };

  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        <div className="flex flex-wrap gap-1 border-b border-divider bg-canvas p-2">
          {tools.map((tool) => (
            <button key={tool.label} type="button" className="grid h-9 w-9 place-items-center rounded-lg text-secondary hover:bg-hover hover:text-ink" onClick={() => wrapSelection(tool.before, tool.after)} title={tool.label} aria-label={tool.label}>
              {"icon" in tool && tool.icon ? <ToolbarIcon icon={tool.icon} /> : <span className="text-xs font-bold">{tool.text}</span>}
            </button>
          ))}
          <button type="button" className="grid h-9 w-9 place-items-center rounded-lg text-secondary hover:bg-hover hover:text-ink" onClick={insertLink} title="Insert link" aria-label="Insert link">
            <Link2 className="h-4 w-4" />
          </button>
          <button type="button" className="grid h-9 w-9 place-items-center rounded-lg text-secondary hover:bg-hover hover:text-ink" onClick={() => fileRef.current?.click()} title="Attach file" aria-label="Attach file">
            <Paperclip className="h-4 w-4" />
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            onChange={(event) => {
              setFiles(Array.from(event.target.files ?? []).map((file) => file.name));
              event.currentTarget.value = "";
            }}
          />
        </div>
        <textarea
          ref={inputRef}
          className={`${compact ? "min-h-36" : "min-h-44"} w-full resize-y bg-surface px-3 py-3 text-sm leading-6 outline-none`}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={placeholder ?? "Share a practical note, question or proposed wording. Avoid client names and privileged details."}
        />
      </div>
      {files.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {files.map((file) => (
            <span key={file} className="inline-flex items-center gap-1.5 rounded-md border border-line bg-canvas px-2 py-1 text-xs text-secondary">
              <Paperclip className="h-3 w-3" />
              {file}
            </span>
          ))}
        </div>
      ) : null}
      {!compact ? <p className="mt-2 text-xs leading-5 text-muted">Formatting is saved as structured text. File upload is prepared here; connect storage when documents should be persisted.</p> : null}
    </div>
  );
}

function ToolbarIcon({ icon: Icon }: { icon: typeof Bold }) {
  return <Icon className="h-4 w-4" />;
}
